/**
 * useAudioUpload Hook
 *
 * React hook for uploading audio files with multipart upload support.
 * Handles large files by chunking, tracks progress, and supports pause/resume/cancel.
 */

import { useState, useCallback, useRef } from "react";
import {
  initiateMultipartUpload,
  completeMultipartUpload,
  abortMultipartUpload,
} from "@/services/reflections";
import type {
  ReflectionResponse,
  MultipartUploadSession,
  CompletedPart,
  ExpectedSpeakers,
} from "@/types/reflection";

/**
 * Upload state tracked by the hook
 */
export interface UploadState {
  status:
    | "idle"
    | "validating"
    | "uploading"
    | "paused"
    | "completed"
    | "error";
  progress: number;
  currentPart: number;
  totalParts: number;
  bytesUploaded: number;
  totalBytes: number;
  eta: number | null;
  error: string | null;
  reflection: ReflectionResponse | null;
}

/**
 * Options for the useAudioUpload hook
 */
export interface UseAudioUploadOptions {
  /** Chunk size for multipart upload (default 10MB) */
  chunkSize?: number;
  /** Maximum file size allowed (default 2GB) */
  maxFileSize?: number;
  /** Callback when progress updates */
  onProgress?: (state: UploadState) => void;
  /** Callback when upload completes successfully */
  onComplete?: (reflection: ReflectionResponse) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
}

const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const ALLOWED_MIME_TYPES = ["audio/m4a", "audio/mp4", "audio/x-m4a"];

/**
 * Hook for uploading audio files with multipart upload support
 */
export function useAudioUpload(options: UseAudioUploadOptions = {}) {
  const {
    chunkSize = DEFAULT_CHUNK_SIZE,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    onProgress,
    onComplete,
    onError,
  } = options;

  const [state, setState] = useState<UploadState>({
    status: "idle",
    progress: 0,
    currentPart: 0,
    totalParts: 0,
    bytesUploaded: 0,
    totalBytes: 0,
    eta: null,
    error: null,
    reflection: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionRef = useRef<MultipartUploadSession | null>(null);
  const isPausedRef = useRef(false);
  const completedPartsRef = useRef<CompletedPart[]>([]);
  const startTimeRef = useRef<number>(0);

  /**
   * Validate file type and size
   */
  const validateFile = useCallback(
    (file: File): string | null => {
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        return `Unsupported file type: ${file.type}. Please upload an M4A file.`;
      }
      if (file.size > maxFileSize) {
        const fileSizeGB = (file.size / 1024 / 1024 / 1024).toFixed(2);
        const maxSizeGB = (maxFileSize / 1024 / 1024 / 1024).toFixed(2);
        return `File too large: ${fileSizeGB}GB. Maximum: ${maxSizeGB}GB`;
      }
      return null;
    },
    [maxFileSize]
  );

  /**
   * Update state and trigger onProgress callback
   */
  const updateState = useCallback(
    (updates: Partial<UploadState>) => {
      setState((prev) => {
        const newState = { ...prev, ...updates };
        onProgress?.(newState);
        return newState;
      });
    },
    [onProgress]
  );

  /**
   * Calculate estimated time remaining
   */
  const calculateEta = useCallback(
    (bytesUploaded: number, totalBytes: number): number | null => {
      if (bytesUploaded === 0) return null;
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const rate = bytesUploaded / elapsed;
      if (rate === 0) return null;
      const remaining = totalBytes - bytesUploaded;
      return Math.ceil(remaining / rate);
    },
    []
  );

  /**
   * Upload a single part to S3 using pre-signed URL
   */
  const uploadPart = useCallback(
    async (url: string, chunk: Blob, partNumber: number): Promise<string> => {
      const response = await fetch(url, {
        method: "PUT",
        body: chunk,
        signal: abortControllerRef.current?.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Part ${partNumber} upload failed: ${response.statusText}`
        );
      }

      const etag = response.headers.get("ETag");
      if (!etag) {
        console.error(
          `Part ${partNumber} missing ETag header. This is likely a CORS issue.`
        );
        console.error(
          `Available headers:`,
          Array.from(response.headers.entries())
        );
        throw new Error(
          `Part ${partNumber} missing ETag header. The S3 bucket may not have CORS configured properly. Please check CORS settings.`
        );
      }

      return etag;
    },
    []
  );

  /**
   * Perform multipart upload
   */
  const uploadMultipart = useCallback(
    async (
      file: File,
      title?: string,
      description?: string,
      userContext?: string,
      therapyStyleId?: string,
      expectedSpeakers?: ExpectedSpeakers,
      conversationPartnerId?: string | null,
      isWithPrimaryPartner?: boolean
    ): Promise<ReflectionResponse> => {
      startTimeRef.current = Date.now();
      completedPartsRef.current = [];

      // Initiate multipart upload
      const session = await initiateMultipartUpload(
        file.name,
        file.size,
        file.type,
        {
          title,
          description,
          userContext,
          chunkSize,
          therapyStyleId,
          expectedSpeakers: expectedSpeakers as
            | Record<string, unknown>
            | undefined,
          conversationPartnerId,
          isWithPrimaryPartner,
        }
      );
      sessionRef.current = session;

      updateState({
        totalParts: session.total_parts,
        totalBytes: file.size,
      });

      // Upload each part
      for (let i = 0; i < session.part_urls.length; i++) {
        // Check for pause
        while (isPausedRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Check for abort
        if (abortControllerRef.current?.signal.aborted) {
          throw new Error("Upload cancelled");
        }

        const partInfo = session.part_urls[i];
        const start = (partInfo.part_number - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const etag = await uploadPart(
          partInfo.url,
          chunk,
          partInfo.part_number
        );

        completedPartsRef.current.push({
          part_number: partInfo.part_number,
          etag,
        });

        const bytesUploaded = end;
        const progress = (bytesUploaded / file.size) * 100;

        updateState({
          currentPart: partInfo.part_number,
          bytesUploaded,
          progress,
          eta: calculateEta(bytesUploaded, file.size),
        });
      }

      // Complete upload
      const response = await completeMultipartUpload(
        session.reflection_id,
        completedPartsRef.current
      );

      return response.reflection;
    },
    [chunkSize, updateState, calculateEta, uploadPart]
  );

  /**
   * Start upload process
   */
  const upload = useCallback(
    async (
      file: File,
      title?: string,
      description?: string,
      userContext?: string,
      therapyStyleId?: string,
      expectedSpeakers?: ExpectedSpeakers,
      conversationPartnerId?: string | null,
      isWithPrimaryPartner?: boolean
    ) => {
      // Reset state
      updateState({
        status: "validating",
        progress: 0,
        currentPart: 0,
        totalParts: 0,
        bytesUploaded: 0,
        totalBytes: file.size,
        eta: null,
        error: null,
        reflection: null,
      });

      abortControllerRef.current = new AbortController();
      isPausedRef.current = false;

      try {
        // Validate file
        const validationError = validateFile(file);
        if (validationError) {
          throw new Error(validationError);
        }

        updateState({ status: "uploading" });

        // Perform multipart upload
        const reflection = await uploadMultipart(
          file,
          title,
          description,
          userContext,
          therapyStyleId,
          expectedSpeakers,
          conversationPartnerId,
          isWithPrimaryPartner
        );

        updateState({
          status: "completed",
          progress: 100,
          reflection,
        });

        onComplete?.(reflection);
        return reflection;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        updateState({
          status: "error",
          error: errorMessage,
        });
        onError?.(errorMessage);
        throw error;
      }
    },
    [validateFile, updateState, uploadMultipart, onComplete, onError]
  );

  /**
   * Pause upload
   */
  const pause = useCallback(() => {
    isPausedRef.current = true;
    updateState({ status: "paused" });
  }, [updateState]);

  /**
   * Resume paused upload
   */
  const resume = useCallback(() => {
    isPausedRef.current = false;
    updateState({ status: "uploading" });
  }, [updateState]);

  /**
   * Cancel upload and clean up
   */
  const cancel = useCallback(async () => {
    abortControllerRef.current?.abort();

    if (sessionRef.current) {
      try {
        await abortMultipartUpload(sessionRef.current.reflection_id);
      } catch {
        // Ignore abort errors
      }
    }

    updateState({
      status: "idle",
      progress: 0,
      error: null,
    });
  }, [updateState]);

  /**
   * Reset state to initial values
   */
  const reset = useCallback(() => {
    abortControllerRef.current = null;
    sessionRef.current = null;
    isPausedRef.current = false;
    completedPartsRef.current = [];
    startTimeRef.current = 0;

    setState({
      status: "idle",
      progress: 0,
      currentPart: 0,
      totalParts: 0,
      bytesUploaded: 0,
      totalBytes: 0,
      eta: null,
      error: null,
      reflection: null,
    });
  }, []);

  return {
    state,
    upload,
    pause,
    resume,
    cancel,
    reset,
    validateFile,
  };
}
